import { Component, inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { VehiculoService } from '@app/core/services/vehiculo.service';
import { Distrito } from '@app/models/distrito';
import { Empresa } from '@app/models/empresa';
import { Sector } from '@app/models/sector';
import { TipoVehiculo } from '@app/models/tipo-vehiculo';
import { Vehiculo } from '@app/models/vehiculo';
import { MessageService } from 'primeng/api';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { FlotaOpciones } from '../nueva-flota/opciones';
import { getYears } from '@app/core/tools/get-years';

@Component({
    selector: 'app-editar-flota',
    standalone: false,
    templateUrl: './editar-flota.component.html',
    styleUrl: './editar-flota.component.scss'
})
export class EditarFlotaComponent {
    private readonly maxIntValue = 2_147_483_647;
    private readonly messageService = inject(MessageService);
    private readonly config = inject(DynamicDialogConfig, { optional: true });
    private readonly ref = inject(DynamicDialogRef);
    private readonly service = inject(VehiculoService);

    isPropio = false;

    flota!: Vehiculo;

    opcionesSelect: FlotaOpciones = {
        titularidades: [
            { label: '--Seleccione--', value: '' },
            { label: 'Alquilado', value: 'Alquilado' },
            { label: 'Propio', value: 'Propio' }
        ],
        tipos: [],
        marcas: [],
        sectores: [],
        distritos: [],
        empresas: [],
        anios: [{ label: '--Seleccione--', value: '' }],
        combustibles: [
            { label: '--Seleccione--', value: '' },
            { label: 'Nafta', value: 'Nafta' },
            { label: 'Gasoil', value: 'Gasoil' }
        ],
        tacografos: [
            { label: 'RSV', value: 'RSV' },
            { label: 'MicroTrack', value: 'MicroTrack' }
        ]
    };

    async ngOnInit() {
        this.cargarOpciones();
    }

    form = new FormGroup({
        titularidad: new FormControl<{ label: string; value: string } | string | null>(null, { nonNullable: true, validators: [Validators.required] }), // Da si es propio o no
        empresa: new FormControl<Empresa | null>(null, { nonNullable: true, validators: [Validators.required] }),
        interno: new FormControl<string>('', { nonNullable: true, validators: [Validators.required, Validators.max(this.maxIntValue)] }),
        dominio: new FormControl<string>(''),
        motor: new FormControl<string>(''),
        chasis: new FormControl<string>(''),
        tipo_vehiculo: new FormControl<TipoVehiculo | null>(null, { nonNullable: true, validators: [Validators.required] }),
        identificador_tacografo: new FormControl<string>(''),
        marca: new FormControl<{ id: string; nombre: string } | null>(null, { nonNullable: true, validators: [Validators.required] }),
        sector: new FormControl<Sector | null>(null, { nonNullable: true, validators: [Validators.required] }),
        distrito: new FormControl<Distrito | null>(null, { nonNullable: true, validators: [Validators.required] }),
        modelo: new FormControl<string>('', { nonNullable: true, validators: [Validators.required] }),
        anio: new FormControl<{ label: string; value: string } | string | null>(null, { nonNullable: true, validators: [Validators.required] }),
        tipo_combustible: new FormControl<string | null>(null), // Da el string para combustible
        tacografo: new FormControl<string | null>(null), // Da el string para marca_tacografo
        asientos: new FormControl<string>(''),
        observaciones: new FormControl<string>('')
    });

    confirmar() {
        if (!this.form.valid) {
            this.form.markAllAsTouched();
            return;
        }

        const id = this.config?.data.flota.id;

        const fv = this.form.controls;

        const payload: Partial<Vehiculo> = {
            titularidad: fv.titularidad.value as string,
            dominio: fv.dominio.value,
            marca: fv.marca.value?.nombre,
            modelo: fv.modelo.value,
            identificador_tacografo: fv.identificador_tacografo.value,
            propio: fv.titularidad.value === 'Propio' ? 1 : 0,
            marca_tacografo: fv.tacografo.value,
            observaciones: fv.observaciones.value,
            combustible: fv.tipo_combustible.value,
            nro_interno: +fv.interno.value,
            nro_motor: fv.motor.value ? +fv.motor.value : undefined,
            nro_chasis: fv.chasis.value ? +fv.chasis.value : undefined,
            anio: fv.anio.value ? +fv.anio.value : undefined,
            capacidad: fv.asientos.value ? +fv.asientos.value : undefined,
            distrito: fv.distrito.value!,
            sector: fv.sector.value!,
            empresa: fv.empresa.value!,
            tipo_vehiculo: fv.tipo_vehiculo.value!
        };
        console.log(payload);

        this.service.update(id, payload).subscribe({
            next: (data) => {
                console.log(data);
                this.ref.close(data);
            },
            error: (err) => {
                console.error(err);
                this.messageService.add({ severity: 'error', summary: 'Error', detail: this.resolveVehiculoErrorMessage(err) });
            }
        });
    }

    cancelar() {
        this.ref.close();
    }

    cargarOpciones() {
        // Obtenemos los datos iniciales
        this.service.initForm().subscribe({
            next: ({ tipos, marcas, sectores, distritos, empresas }) => {
                this.opcionesSelect.tipos = tipos;
                this.opcionesSelect.marcas = marcas;
                this.opcionesSelect.sectores = sectores;
                this.opcionesSelect.distritos = distritos;
                this.opcionesSelect.empresas = empresas;

                const years = getYears(2000).map((year) => ({ label: year.toString(), value: year.toString() }));

                this.opcionesSelect.anios = [...this.opcionesSelect.anios, ...years];

                // Escucha cambios en el select de titularidad
                this.form.get('titularidad')?.valueChanges.subscribe((titularidad) => {
                    this.isPropio = false;
                    // resetear cliente si cambia la titularidad
                    this.form.get('empresa')?.reset();
                    if (titularidad == 'Propio') {
                        this.isPropio = true;
                        const cnsapag = empresas.find((empresa) => empresa.nombre == 'CN Sapag') || null;
                        this.form.get('empresa')?.patchValue(cnsapag);
                    }
                });

                const id = this.config?.data.flota.id;
                this.service.getOne(id).subscribe({
                    next: (data) => {
                        this.flota = data;
                        this.form.patchValue({
                            ...data,
                            interno: (data.nro_interno ?? '') as string,
                            chasis: (data.nro_chasis ?? '') as string,
                            motor: (data.nro_motor ?? '') as string,
                            asientos: (data.capacidad ?? '') as string,
                            anio: this.opcionesSelect.anios.find((anio) => anio.value == ((data.anio ?? '') as string))?.value,
                            titularidad: this.opcionesSelect.titularidades.find((t) => t.value == data.titularidad)?.value,
                            tipo_combustible: this.opcionesSelect.combustibles.find((c) => c.value == data.combustible)?.value,
                            tacografo: this.opcionesSelect.tacografos.find((t) => t.value == data.marca_tacografo)?.value,
                            empresa: this.opcionesSelect.empresas.find((empresa) => empresa.id == (data.empresa?.id ?? '')),
                            tipo_vehiculo: this.opcionesSelect.tipos.find((tipo) => tipo.id == (data.tipo_vehiculo?.id ?? '')),
                            marca: this.opcionesSelect.marcas.find((marca) => marca.nombre == (data.marca ?? '')),
                            sector: this.opcionesSelect.sectores.find((sector) => sector.id == (data.sector?.id ?? '')),
                            distrito: this.opcionesSelect.distritos.find((distrito) => distrito.id == (data.distrito?.id ?? ''))
                        });
                    },
                    error: (err) => {
                        console.error('Error al traer el vehículo', err);
                    }
                });
            },
            error: (err) => {
                console.error('Error cargando opciones', err);
                this.messageService.add({ severity: 'error', summary: 'Error', detail: 'No se pudieron cargar las opciones.' });
            }
        });
    }

    private resolveVehiculoErrorMessage(err: unknown): string {
        const body = (err as any)?.error ?? err;
        const sql = body?.sqlMessage ?? body?.driverError?.sqlMessage;
        const text = `${body?.msg ?? body?.message ?? sql ?? ''}`.toLowerCase();

        if (text.includes('out of range') && text.includes('nro_interno')) {
            return `El Número de Interno es demasiado grande. Debe ser menor o igual a ${this.maxIntValue}.`;
        }

        return (err as any)?.error?.msg || (err as any)?.message || 'No se pudo editar la unidad.';
    }
}
